import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { apiurl } from "../../Constants";



function Payment() {
  const location = useLocation();

  // Original complaint_id parsing preserved (e.g. ?123)
  const searchParams = new URLSearchParams(location.search);
  const complaint_id =
    searchParams.get("complaint_id") || location.search.slice(1).split("&")[0];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    fetchDetails();
    checkForSenangPayReturn();
  }, [complaint_id]);

  const fetchDetails = async () => {
    try {
      const res = await fetch(`${apiurl}complaint/excess/product/details`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ complaint_id }),
      });
      const result = await res.json();
      if (result.code === 200) {
        setData(result.data);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Reads the payment gateway's return-URL query params (if the browser was
  // just redirected back here after payment) and shows the result screen.
  const checkForSenangPayReturn = () => {
    const status_id = searchParams.get("status_id");
    const order_id = searchParams.get("order_id");
    const transaction_id = searchParams.get("transaction_id");
    const msg = searchParams.get("msg");

    if (!status_id || !order_id) return;

    if (String(status_id) === "1") {
      setPaymentResult({
        status: "success",
        orderId: order_id,
        transactionId: transaction_id,
        msg: msg || "Your payment was successful!",
      });
    } else if (String(status_id) === "3") {
      setPaymentResult({
        status: "failed",
        orderId: order_id,
        transactionId: transaction_id,
        msg: msg || "Payment failed. Please try again.",
      });
    } else {
      setPaymentResult({
        status: "pending",
        orderId: order_id,
        transactionId: transaction_id,
        msg: msg || "Payment received but verification is pending. We'll confirm shortly.",
      });
    }
  };

  const handlePayNow = async () => {
    try {
      setPaymentLoading(true);

      // Place the excess order.
      // Backend places the order AND builds the DOKU checkout session,
      // returning a ready-to-use payment URL. Just redirect to it.
      const total = data.product_details.reduce(
        (sum, item) => sum + Number(item.total_price),
        0
      );
      const shipping_amount = Number(data.grand_total) - total;

      const orderPayload = {
        complaint_id: complaint_id,
        user_id: data.address_details?.user_id,
        address_id: data.address_details?.id,
        product_details: data.product_details,
        total: total,
        shipping_amount: shipping_amount >= 0 ? shipping_amount : 0,
        grand_total: data.grand_total,
        weight: data.weight,
        order_through: "web",
      };

      const orderRes = await axios.post(
        `${apiurl}complaint/excess/order_place`,
        orderPayload
      );

      const { order_id, url } = orderRes.data || {};

      if (!order_id || !url) {
        toast.error("Failed to place. Please try again.");
        setPaymentLoading(false);
        return;
      }

      // Redirect browser to DOKU's hosted checkout page
      window.location.href = url;
      // Browser navigates away here — paymentLoading state no longer matters,
      // the result screen is shown on return via checkForSenangPayReturn().
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Something went wrong. Please try again.");
      setPaymentLoading(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3">Processing Request...</p>
      </div>
    );
  }

  // ── In-page payment result screen (shown after gateway redirect return) ──
  if (paymentResult) {
    const isSuccess = paymentResult.status === "success";
    const isFailed  = paymentResult.status === "failed";
    const isPending = paymentResult.status === "pending";

    const borderColor = isSuccess ? "#28a745" : isFailed ? "#dc3545" : "#ffc107";
    const titleColor  = isSuccess ? "#28a745" : isFailed ? "#dc3545" : "#856404";

    return (
      <div className="payment-page">
        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div
                className="text-center p-5 bg-white rounded shadow-sm"
                style={{ border: `2px solid ${borderColor}` }}
              >
                {/* Icon */}
                <div className="mb-3" style={{ fontSize: "3.5rem", lineHeight: 1 }}>
                  {isSuccess && "✅"}
                  {isFailed  && "❌"}
                  {isPending && "⏳"}
                </div>

                {/* Title */}
                <h3 className="fw-bold mb-2" style={{ color: titleColor }}>
                  {isSuccess && "Payment Successful!"}
                  {isFailed  && "Payment Failed"}
                  {isPending && "Payment Pending"}
                </h3>

                <p className="text-muted mb-4">{paymentResult.msg}</p>

                {/* Details */}
                <div className="text-start bg-light rounded p-3 mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Order ID</span>
                    <span className="fw-semibold small">{paymentResult.orderId}</span>
                  </div>
                  {paymentResult.transactionId && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted small">Transaction ID</span>
                      <span
                        className="fw-semibold small"
                        style={{ wordBreak: "break-all", maxWidth: "60%", textAlign: "right" }}
                      >
                        {paymentResult.transactionId}
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between">
                    <span className="text-muted small">Amount</span>
                    <span className="fw-semibold small">RM {data?.grand_total}</span>
                  </div>
                </div>

                {/* Retry button only on failure */}
                {isFailed && (
                  <button
                    className="btn w-100 py-3 fw-bold"
                    style={{ backgroundColor: "#ffae00" }}
                    onClick={() => setPaymentResult(null)}
                  >
                    Try Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return <div className="text-center mt-5">No data found.</div>;

  // ── Main checkout page ─────────────────────────────────────────────────────
  return (
    <div className="payment-page">
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10">

            {/* <header className="text-center mb-5">
              <h2 className="fw-bold text-dark">Secure Checkout</h2>
              <p className="text-muted">Review your complaint details and complete the payment</p>
            </header> */}

            <div className="row g-4">
              {/* Left Column: Address & Products */}
              <div className="col-md-8">

                {/* Address Card */}
                <div className="payment-card mb-4">
                  <div className="card-header-custom">
                    <i className="bi bi-geo-alt-fill me-2"></i> Delivery Address
                  </div>
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-2">
                      {data.address_details.first_name} {data.address_details.last_name}
                    </h5>
                    <p className="address-text mb-1">{data.address_details.address}</p>
                    <p className="address-text mb-1">
                      {data.address_details.city_name}, {data.address_details.state_name} -{" "}
                      <strong>{data.address_details.pin_code}</strong>
                    </p>
                    <hr />
                    <div className="d-flex flex-wrap gap-3">
                      <span className="contact-info">
                        <small className="d-block text-muted">Phone</small>
                        {data.address_details.phone_number}
                      </span>
                      <span className="contact-info">
                        <small className="d-block text-muted">Email</small>
                        {data.address_details.email_id}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Products Table */}
                <div className="payment-card overflow-hidden">
                  <div className="card-header-custom">
                    <i className="bi bi-box-seam-fill me-2"></i> Product Details
                  </div>
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">Product</th>
                          <th className="text-center">Qty</th>
                          <th className="text-end pe-4">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.product_details.map((item, index) => (
                          <tr key={index}>
                            <td className="ps-4 py-3">
                              <span className="d-block fw-semibold">{item.product_name}</span>
                              <small className="text-muted">Unit Price: RM {item.price}</small>
                            </td>
                            <td className="text-center">{item.quantity}</td>
                            <td className="text-end pe-4 fw-bold">RM {item.total_price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>

              {/* Right Column: Order Summary */}
              <div className="col-md-4">
                <div className="summary-card sticky-top" style={{ top: "2rem" }}>

                  <div className="mb-3">
                    {data.total && (
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Subtotal</span>
                        <span>RM {data.total}</span>
                      </div>
                    )}
                    {data.shipping_amount && (
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Shipping</span>
                        <span>RM {data.shipping_amount}</span>
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <span className="h5 mb-0">Total:</span>
                    <span className="h4 mb-0 fw-bold">RM {data.grand_total}</span>
                  </div>

                  <button
                    className="btn w-100 py-3 fw-bold shadow-sm"
                    style={{ backgroundColor: "#ffae00" }}
                    onClick={handlePayNow}
                    disabled={paymentLoading}
                  >
                    {paymentLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Processing...
                      </>
                    ) : (
                      "Proceed to Pay Now"
                    )}
                  </button>

                </div>
              </div>

            </div>
          </div>


        </div>
      </div>


   </div>
  );
}

export default Payment;